
import Vue from "vue";
import { mapState } from "vuex";
import { auth } from "@/utils";
import OAuthLoginPanel from "@/components/OAuthLoginPanel.vue";
import {
  completeAuthorization,
  completeOAuthProvider,
  getAuthorizeContext,
  getOAuthErrorMessage,
  getOAuthProviderURL,
  loginOAuthByEmail,
  loginOAuthByPhone,
  sendOAuthPhoneCode,
  OAuthAuthorizeContext,
  OAuthAuthorizeQuery,
} from "@/api/oauth";

function queryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return queryValue(value[0]);
  }
  return typeof value === "string" ? value : "";
}

export default Vue.extend({
  components: { OAuthLoginPanel },
  data() {
    return {
      context: null as OAuthAuthorizeContext | null,
      loading: false,
      error: "",
      phone: "",
      code: "",
      email: "",
      password: "",
      time: 0,
    };
  },
  computed: {
    ...mapState("app", {
      isCN: "isCN",
      isMobile: "isMobile",
      isInWeChat: "isInWeChat",
    }),
    ...mapState("assets", { images: "images" }),
    phoneHelp(): string {
      if (this.phone && !/^1[3456789]\d{9}$/.test(this.phone)) {
        return "" + this.$t("inputError") + this.$t("phone");
      }
      return "";
    },
    emailHelp(): string {
      if (
        this.email &&
        !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/.test(this.email)
      ) {
        return "" + this.$t("inputError") + this.$t("email");
      }
      return "";
    },
    canSendCode(): boolean {
      return !this.loading && !this.time && !!this.phone && !this.phoneHelp;
    },
    canPhoneSignIn(): boolean {
      return !this.loading && !!this.phone && !this.phoneHelp && !!this.code;
    },
    canEmailSignIn(): boolean {
      return (
        !this.loading && !!this.email && !this.emailHelp && !!this.password
      );
    },
  },
  mounted() {
    void this.loadAuthorization();
  },
  methods: {
    authorizeQuery(): OAuthAuthorizeQuery {
      const query = this.$route.query;
      return {
        client_id: queryValue(query.client_id),
        response_type: queryValue(query.response_type),
        redirect_uri: queryValue(query.redirect_uri),
        scope: queryValue(query.scope),
        state: queryValue(query.state),
        code_challenge: queryValue(query.code_challenge),
        code_challenge_method: queryValue(query.code_challenge_method),
      };
    },
    async loadAuthorization() {
      try {
        this.context = await getAuthorizeContext(this.authorizeQuery());
        const channel = queryValue(this.$route.query.oauth_channel);
        const providerCode = queryValue(this.$route.query.code);
        if (channel && providerCode) {
          await this.finish(await completeOAuthProvider(channel, providerCode));
          return;
        }
        const existingToken = auth.getToken();
        if (existingToken) {
          await this.finish(existingToken);
        }
      } catch (cause) {
        this.error = getOAuthErrorMessage(cause);
      }
    },
    async onSendCode() {
      if (!this.canSendCode) return;
      this.error = "";
      try {
        await sendOAuthPhoneCode(this.phone);
        this.time = 59;
        const timer = window.setInterval(() => {
          this.time -= 1;
          if (this.time <= 0) {
            window.clearInterval(timer);
            this.time = 0;
          }
        }, 1000);
      } catch (cause) {
        this.error = getOAuthErrorMessage(cause);
      }
    },
    async onPhoneSignIn() {
      if (this.canPhoneSignIn)
        await this.runLogin(() => loginOAuthByPhone(this.phone, this.code));
    },
    async onEmailSignIn() {
      if (this.canEmailSignIn)
        await this.runLogin(() => loginOAuthByEmail(this.email, this.password));
    },
    async runLogin(login: () => Promise<string>) {
      this.loading = true;
      this.error = "";
      try {
        await this.finish(await login());
      } catch (cause) {
        this.error = getOAuthErrorMessage(cause);
      } finally {
        this.loading = false;
      }
    },
    async finish(accessToken: string) {
      if (!this.context) throw new Error("授权上下文不存在");
      this.loading = true;
      const result = await completeAuthorization(
        this.context.transaction_id,
        accessToken
      );
      window.location.href = result.redirect_url;
    },
    async onProvider(channel: string) {
      if (this.loading) return;
      if (channel === "wechat" && this.isMobile && !this.isInWeChat) {
        this.$message.info(this.$t("openInWeChat") + "");
        return;
      }
      const provider =
        channel === "wechat" && this.isInWeChat
          ? "wechat_oa_userinfo"
          : channel;
      this.loading = true;
      this.error = "";
      try {
        const callbackURL = new URL(window.location.href);
        callbackURL.searchParams.set("oauth_channel", provider);
        callbackURL.searchParams.delete("code");
        window.location.href = await getOAuthProviderURL(
          provider,
          callbackURL.toString()
        );
      } catch (cause) {
        this.error = getOAuthErrorMessage(cause);
        this.loading = false;
      }
    },
    onSignUp() {
      this.$router.push({
        path: this.$paths.signUp,
        query: { ...this.$route.query, redirect: window.location.href },
      });
    },
    onReset() {
      this.$router.push({
        path: this.$paths.reset,
        query: { ...this.$route.query, redirect: window.location.href },
      });
    },
  },
});
